import Vue from 'vue'

import { cloneDeep, get, isEqual } from 'lodash'

import { Services } from '@sigma-frontend-libs/commons'
import { CSendingTypesGenerallyAvailable } from '@sigmasms/commons'

import { generateServices, globalErrorHandler, states } from '@/utils'

import { ruleProcessor } from '@/components/services/tariffs/utils'
import { localServices } from '@/components/trees/routes/utils'

import render from './render'

export default {
  name: 'RoutesTree',

  mixins: [
    generateServices([
      {
        name: Services.routingGroups,

        find: false,
        create: false,
        update: false,
        remove: false
      },
      {
        name: Services.routingRules,

        find: false,
        create: false,
        update: false,
        remove: false
      },
      {
        name: Services.providerGroups,

        find: false,
        create: false,
        update: false,
        remove: false
      }
    ])
  ],

  props: {
    value: {
      type: Object,
      default: () => {
        return {}
      }
    },

    highlight: {
      type: Object,
      default: () => {
        return {}
      }
    },

    readonly: Boolean
  },

  data() {
    const showDialog = {
      full: {},
      direct: {}
    }
    const showAddRouteMenu = {}

    const RoutingGroups = {}
    const initRoutingGroups = {}
    for (const type of CSendingTypesGenerallyAvailable) {
      showDialog.full[type] = false
      showDialog.direct[type] = false
      showAddRouteMenu[type] = false

      RoutingGroups[type] = []
      initRoutingGroups[type] = []
    }

    return {
      showDialog,
      showAddRouteMenu,
      showConfirmRemoveDialog: {},

      routeSelector: undefined,
      loading: false,
      fillRoutingGroupsRequestId: 0,
      loadingFillRoutingGroupsRequestId: 0,

      rowState: {},

      RoutingGroups,
      initRoutingGroups,

      RoutingRules: {},
      ProviderGroups: {},
      Providers: {},

      pendingClick: 0
    }
  },

  computed: {
    hasSelectedRoutingGroups() {
      for (const type of CSendingTypesGenerallyAvailable) {
        if (Array.isArray(this.value?.[type]) && this.value[type].length) {
          return true
        }
      }

      return false
    },

    hasRoutingGroups() {
      for (const type of CSendingTypesGenerallyAvailable) {
        if (Array.isArray(this.RoutingGroups[type]) && this.RoutingGroups[type].length) {
          return true
        }
      }

      return false
    },

    types() {
      const result = this.getSendingTypesByPermission(`advanced.${Services.users}.setRoutingGroups`)

      return result.map(value => {
        return {
          title: `${Services.sendings}.types.${value}`,
          value
        }
      })
    }
  },

  watch: {
    value: {
      handler() {
        if (!isEqual(this.normalizeRoutingGroups(this.value), this.normalizeRoutingGroups(this.RoutingGroups))) {
          this.initRoutingGroups = cloneDeep(this.normalizeRoutingGroups(this.value))
          this.fillRoutingGroups()
        }
      },
      deep: true
    },
    RoutingGroups: {
      handler() {
        const normalizedValue = this.normalizeRoutingGroups(this.value)
        const normalizedRoutingGroups = this.normalizeRoutingGroups(this.RoutingGroups)
        if (!isEqual(normalizedValue, normalizedRoutingGroups)) {
          this.$emit('input', normalizedRoutingGroups)
        }
      },
      deep: true
    }
  },

  async mounted() {
    const filledRoutingGroups = await this.fillRoutingGroups()
    if (filledRoutingGroups && Object.keys(filledRoutingGroups).length) {
      this.initRoutingGroups = cloneDeep(this.normalizeRoutingGroups(filledRoutingGroups))
    }

    await this.openingActiveRoute()
  },

  methods: {
    normalizeRoutingGroups(value) {
      const result = {}
      for (const type of CSendingTypesGenerallyAvailable) {
        if (Array.isArray(value?.[type])) {
          result[type] = value[type].map(item => {
            return { id: item.id }
          })
        } else {
          result[type] = []
        }
      }

      return result
    },

    async fillRoutingGroups(inside = false) {
      const requestId = this.fillRoutingGroupsRequestId + 1
      this.fillRoutingGroupsRequestId = requestId

      let source = this.value
      if (inside) {
        source = this.RoutingGroups
      }
      const data = cloneDeep(source)
      const filledRoutingGroups = this.normalizeRoutingGroups(data)

      if (!inside && this.hasSelectedRoutingGroups) {
        this.loadingFillRoutingGroupsRequestId = requestId
        this.loading = true
      }

      try {
        for (const type of CSendingTypesGenerallyAvailable) {
          if (Array.isArray(data?.[type])) {
            filledRoutingGroups[type] = await Promise.all(data[type].map(async item => {
              const lastState = get(this.rowState, `${Services.routingGroups}.${item.id}`, 'closed')
              Vue.set(this.rowState, `${Services.routingGroups}.${item.id}`, 'loading')
              if (!item.title || !item.type || !item.description) {
                try {
                  const routingGroup = await this.rest.routingGroups.get(item.id)
                  Object.assign(item, routingGroup)
                } catch (error) {
                  globalErrorHandler(error)
                }
              }
              Vue.set(this.rowState, `${Services.routingGroups}.${item.id}`, lastState)

              return item
            }))
          }
        }

        if (requestId !== this.fillRoutingGroupsRequestId) {
          return undefined
        }

        this.RoutingGroups = filledRoutingGroups

        return filledRoutingGroups
      } finally {
        if (!inside && requestId === this.loadingFillRoutingGroupsRequestId) {
          this.loading = false
        }
      }
    },

    addRoutingGroup(id, type) {
      if (id) {
        if (!~this.RoutingGroups[type].findIndex(item => {
          return item.id === id
        })) {
          this.RoutingGroups[type].push({ id })
          this.fillRoutingGroups(true)
          this.routeSelector = null
          this.$nextTick(() => {
            this.showAddRouteMenu[type] = false
          })
        }
      }
    },
    removeRoutingGroup(id, type) {
      if (id) {
        const index = this.RoutingGroups[type].findIndex(item => {
          return item.id === id
        })
        if (index > -1) {
          Vue.set(this.showConfirmRemoveDialog, id, false)
          this.RoutingGroups[type].splice(index, 1)
          Vue.delete(this.rowState, `${Services.routingGroups}.${id}`)
        }
      }
    },

    getRowState(service, id, defaults = 'closed') {
      return get(this.rowState, `${service}.${id}`, defaults)
    },

    closeRow(service, id, force = false) {
      if (force || this.getRowState(service, id) != 'closed') {
        Vue.set(this.rowState, `${service}.${id}`, 'closed')

        return this.getRowState(service, id)
      }
    },
    openRow(service, id, force = false) {
      if (force || this.getRowState(service, id) != 'opened') {
        Vue.set(this.rowState, `${service}.${id}`, 'opened')

        return this.getRowState(service, id)
      }
    },
    clickRow(event, options) {
      if (this.pendingClick) {
        clearTimeout(this.pendingClick)
        this.pendingClick = 0
      }

      switch (event.detail) {
        case 1: {
          this.pendingClick = setTimeout(() => {
            if (typeof options.click === 'function') {
              options.click()
            }
          }, 300)
          break
        }
        case 2: {
          if (typeof options.dblclick === 'function') {
            options.dblclick()
          }
          break
        }
      }
    },

    async getEntities(service, association, id) {
      if (service && id) {
        if (service === 'providers') {
          return
        }

        switch (this.getRowState(service, id)) {
          case 'closed': {
            try {
              Vue.set(this.rowState, `${service}.${id}`, states.loading)
              const response = await this.rest[service].get(id, { query: { $scope: association } })
              const data = response[association]
              if (!this[association][id]) {
                Vue.set(this[association], id, [])
              }
              this[association][id].splice(0, data.length, ...data)
              if (service === 'routingGroups') {
                this[association][id].map(item => {
                  if (Array.isArray(item.rules) && item.rules.length) {
                    for (const rule of item.rules) {
                      rule.value = ruleProcessor(rule.value, rule.type || rule.tag)
                    }
                  }
                })
              }
              this[association][id].sort((a, b) => {
                return a.priority - b.priority || a.percentage - b.percentage
              })

              return data
            } catch (error) {
              globalErrorHandler(error)
            } finally {
              this.openRow(service, id)
            }
            break
          }
          case 'opened': {
            this.closeRow(service, id)
            break
          }
        }
      }
    },

    async getAllEntities(service, association, id) {
      if (service && id) {
        try {
          if (localServices[service].hasDeep) {
            const entities = await this.getEntities(service, association, id)
            if (entities) {
              await Promise.all(entities.map(entity => {
                this.closeRow(localServices[service].nextService, entity.id, true)

                return this.getAllEntities(localServices[service].nextService, localServices[localServices[service].nextService].association, entity.id)
              }))
            }
          }
        } catch (error) {
          globalErrorHandler(error)
        }
      }
    },

    async openingActiveRoute() {
      if (this.highlight && Object.keys(this.highlight).length) {
        for (const key in this.highlight) {
          if (Array.isArray(this.highlight[key]) && this.highlight[key].length && localServices[key].hasDeep) {
            for (const id of this.highlight[key]) {
              await this.getEntities(key, localServices[key].association, id)
            }
            if (this.readonly) {
              this.scrollToActiveGroup()
            }
          }
        }
      }
    },

    scrollToActiveGroup() {
      const activeGroupNode = document.getElementsByClassName(`${Services.routingGroups}-tree-routingGroup--active`)[0]
      if (activeGroupNode) {
        const parent = this.$el.parentElement
        const target = activeGroupNode.closest(`.${Services.routingGroups}-tab__item`)
        if (target && parent) {
          this.$nextTick(() => {
            parent.scrollTop = target.offsetTop - parent.offsetTop - parent.clientHeight / 2 + target.clientHeight / 2
          })
        }
      }
    },

    clearShowAddRouteMenu(currentType) {
      for (const type of CSendingTypesGenerallyAvailable) {
        if (currentType !== type) {
          this.showAddRouteMenu[type] = false
        }
      }
    }
  },

  render
}
